import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import { SharepointApp } from "../../models/ClientConfiguration/SharepointApp";
import { MicrosoftRefreshToken } from "../../models/OrganizationConfiguration/MicrosoftRefreshToken";
import ClientConfigurationsRepository from "../../repositories/ClientConfigurationsRepository";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import FieldError from "../common/FieldError";
import LibraryVersioningPanel from "./LibraryVersioningPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { MASKED_CREDENTIAL } from "../../constants/Credentials";
import { QUERY_KEYS } from "../../constants/QueryKeys";

const fields: {
  name: keyof ClientConfiguration;
  label: string;
  md: number;
  default?: string;
}[] = [
    { name: "sharepointTenantId", label: "SharePoint Tenant ID", md: 6 },
    { name: "sharepointRootSiteName", label: "SharePoint Site Name", md: 6, default: "CloudDocs" },
    { name: "casesSharepointRootDriveName", label: "Cases Root Drive Name", md: 3 },
    { name: "providersSharepointRootDriveName", label: "Providers Root Drive Name", md: 3 },
    { name: "firmDocumentsSharepointRootDriveName", label: "Firm Documents Root Drive Name", md: 3 },
    { name: "templateFilesSharepointRootDriveName", label: "Template Files Root Drive Name", md: 3 },
  ];

interface SharePointSectionProps {
  organizationId: string;
  clientConfigurationId?: string | null;
  disabled?: boolean;
}

const SharePointSection: React.FC<SharePointSectionProps> = ({
  organizationId,
  clientConfigurationId,
  disabled = false
}) => {
  const form = useFormContext<ClientConfiguration>();
  const clientConfigurationsRepository = new ClientConfigurationsRepository();
  const organizationsRepository = new OrganizationsRepository();
  const [microsoftRefreshTokens, setMicrosoftRefreshTokens] = useState<MicrosoftRefreshToken[]>([]);
  const [sharepointApps, setSharepointApps] = useState<SharepointApp[]>([]);

  const sharepointAppName = form.watch("sharepointAppName");
  const sharepointClientId = form.watch("sharepointClientId");
  const sharepointClientSecret = form.watch("sharepointClientSecret");
  const sharepointRefreshTokenId = form.watch("sharepointRefreshTokenId");

  const microsoftRefreshTokensQuery = useQuery({ queryKey: [QUERY_KEYS.MICROSOFT_REFRESH_TOKENS, organizationId], queryFn:
    async () => {
      return await organizationsRepository.getOrganizationMicrosoftRefreshTokens(organizationId);
    }, gcTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnMount: true, });

  useEffect(() => {
    if (microsoftRefreshTokensQuery.data) {
      setMicrosoftRefreshTokens(microsoftRefreshTokensQuery.data);
    }
  }, [microsoftRefreshTokensQuery.data]);

  const sharepointAppsQuery = useQuery({ queryKey: [QUERY_KEYS.SHAREPOINT_APPS], queryFn:
    async () => {
      return await clientConfigurationsRepository.getSharepointApps();
    }, gcTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnMount: true, });

  useEffect(() => {
    if (sharepointAppsQuery.data) {
      setSharepointApps(sharepointAppsQuery.data);
    }
  }, [sharepointAppsQuery.data]);

  const selectedSharepointToken = microsoftRefreshTokens.find(
    (token) => token.id === sharepointRefreshTokenId
  );

  const inferredCustomApp =
    !sharepointAppName && (sharepointClientId || sharepointClientSecret);
  const selectedAppName = sharepointAppName || (inferredCustomApp ? "Custom" : "");
  const isCustomApp = selectedAppName === "Custom" || inferredCustomApp;

  useEffect(() => {
    if (inferredCustomApp) {
      form.setValue("sharepointAppName", "Custom");
    }
  }, [inferredCustomApp]);

  useEffect(() => {
    if (sharepointAppName || inferredCustomApp) return;
    const defaultApp = sharepointApps.find((app) => app.isDefault);
    if (defaultApp) {
      form.setValue("sharepointAppName", defaultApp.name);
    }
  }, [sharepointApps, sharepointAppName, inferredCustomApp]);

  return (
    <div>
      <div className="mb-4">
        <div>
          <Label>SharePoint App</Label>
          <div className={cn(sharepointAppsQuery.isLoading && "opacity-50 pointer-events-none")}>
            <Select
              value={selectedAppName}
              onValueChange={(newValue) => {
                form.setValue("sharepointAppName", newValue || "");
              }}
              disabled={disabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sharepointApps.map((app) => (
                  <SelectItem key={app.name} value={app.name}>
                    {app.isDefault ? `${app.name} (default)` : app.name}
                  </SelectItem>
                ))}
                <SelectItem value="Custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FieldError name="sharepointAppName" />
        </div>
      </div>
      {isCustomApp && (
        <div className="grid grid-cols-12 gap-2 justify-between mb-2">
          <div className="col-span-12 md:col-span-6">
            <div>
              <Label>SharePoint Client ID</Label>
              <Input
                disabled={disabled}
                name="sharepointClientId"
                value={sharepointClientId || ""}
                onChange={(event) =>
                  form.setValue("sharepointClientId", event.target.value)
                }
              />
              <FieldError name="sharepointClientId" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div>
              <Label>SharePoint Client Secret</Label>
              <Input
                disabled={disabled}
                name="sharepointClientSecret"
                type="password"
                autoComplete="off"
                value={sharepointClientSecret || ""}
                onChange={(event) =>
                  form.setValue("sharepointClientSecret", event.target.value)
                }
              />
              <FieldError name="sharepointClientSecret" />
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 gap-2 justify-between">
        {fields.map(({ name, label, md, default: defaultValue }) => (
          <div key={name} className={cn("col-span-12", md === 6 && "md:col-span-6", md === 3 && "md:col-span-3")}>
            <div>
              <Label>{label}</Label>
              <Input
                disabled={disabled}
                name={name.toString()}
                value={form.watch(name as any) || defaultValue || ""}
                onChange={(event) =>
                  form.setValue(name as any, event.target.value)
                }
              />
              <FieldError name={name as string} />
            </div>
          </div>
        ))}
      </div>

      <LibraryVersioningPanel
        organizationId={organizationId}
        clientConfigurationId={clientConfigurationId}
        disabled={disabled}
      />

      {microsoftRefreshTokens.length > 0 && (
        <div className="mt-4">
          <div>
            <Label>Sharepoint Refresh Token (Delegated Permissions)</Label>
            <div className="flex items-center gap-2">
              <div className={cn("flex-1", microsoftRefreshTokensQuery.isLoading && "opacity-50 pointer-events-none")}>
                <Select
                  value={selectedSharepointToken ? selectedSharepointToken.id : undefined}
                  onValueChange={(tokenId) => {
                    const token = microsoftRefreshTokens.find(t => t.id === tokenId);
                    // The portal never holds token values: the server copies the selected token's value on save.
                    form.setValue("sharepointRefreshToken", token ? MASKED_CREDENTIAL : "");
                    form.setValue("sharepointRefreshTokenId", token?.id ?? null);
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a token" />
                  </SelectTrigger>
                  <SelectContent>
                    {microsoftRefreshTokens.length > 0 ? (
                      microsoftRefreshTokens.map((token: MicrosoftRefreshToken) => (
                        <SelectItem key={token.id} value={token.id}>
                          {token.userPrincipalName}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem disabled value="__none__">
                        No tokens available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {selectedSharepointToken && (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="secondary"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    // Clearing the id too: a configuration still pointing at the token would get its value written
                    // back the next time the token is renewed.
                    form.setValue("sharepointRefreshToken", "");
                    form.setValue("sharepointRefreshTokenId", null);
                  }}
                  disabled={disabled}
                  title="Clear selection"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePointSection;
